import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { CopyTooltip } from '~/sotano-kit/primitives/copy-tooltip'
import { format } from '@basehub/utils/format'
import { useToggleState } from '~/hooks/use-toggle-state'
import { useEditorMode } from '../../editor-mode-provider'
import { Button } from '~/sotano-kit/primitives/button'
import { TextAlignLeftIcon } from '@radix-ui/react-icons'

export const CodeSnippetControls = ({
  languageSelector,
  formatting,
  copyContent,
}: {
  languageSelector: {
    value?: string
    onValueChange: (value: string) => void
    isOpen?: boolean
    onOpenChange?: (isOpen: boolean) => void
    options: { value: string; label: string; disabled?: boolean }[]
  }
  formatting?: {
    code: string | (() => string)
    lang: string
    onFormat: (result: string) => void
  }
  copyContent: string | (() => string)
}) => {
  const readOnly = useEditorMode() === 'read'
  const internalToggleState = useToggleState(languageSelector.isOpen)

  return (
    <>
      {formatting && !readOnly && (
        <Button
          unstyled
          focusStyled
          className="!-outline-offset-1 hover:bg-dark-shade h-[30px] cursor-default text-xs font-medium rounded-md text-dark-faint w-full text-left flex items-center py-1 px-2 gap-1 max-w-max"
          onClick={async () => {
            const result = await format(
              typeof formatting.code === 'string'
                ? formatting.code
                : formatting.code(),
              formatting.lang as any
            )
            formatting.onFormat(result)
          }}
        >
          <TextAlignLeftIcon />
          Format
        </Button>
      )}
      <Select
        value={languageSelector.value ?? 'No language'}
        onValueChange={languageSelector.onValueChange}
        open={languageSelector.isOpen ?? internalToggleState.isOn}
        onOpenChange={(v) => {
          languageSelector.onOpenChange?.(v)
          internalToggleState.handleToggle()
        }}
        disabled={readOnly}
      >
        <SelectTrigger
          aria-label="language"
          className="!text-dark-faint hover:!text-dark-dim leading-none !gap-0.5"
          color="transparent"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {languageSelector.options.map((option) => {
            return (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    languageSelector.onValueChange(option.value)
                    languageSelector.onOpenChange?.(false)
                    internalToggleState.handleOff()
                  }
                }}
              >
                {option.label}
                {option.disabled && ' (disabled)'}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      <CopyTooltip copyContent={copyContent} className="p-1.5" />
    </>
  )
}
